import { useState } from 'react';

import {
  FileData,
  HandleDeleteFile,
  HandleRetry,
  HandleUpdatePassword,
  STATUS_FILE,
} from '@/hooks/useHelpfindUploady';

import {
  useDeleteFileMutation,
  useUpdateFilePasswordMutation,
} from '@/api/caseCard/setters';
import Check from '@/assets/check.svg?react';
import Cross from '@/assets/cross.svg?react';
import Padlock from '@/assets/padlock.svg?react';
import Refresh from '@/assets/refresh.svg?react';
import PasswordInput from '@/components/ui/PasswordInput';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/Tooltip';
import { RESPONSIVE_QUERY } from '@/consts';
import { shortenFile } from '@/helpers/shortenEmail';
import cn from '@/utils/cn';

import { useAutoAnimate } from '@formkit/auto-animate/react';
import { Trans } from '@lingui/react';
import prettyBytes from 'pretty-bytes';
import { useMediaQuery } from 'react-responsive';

interface Props {
  item: FileData;
  handleDeleteFile: HandleDeleteFile;
  handleRetry: HandleRetry;
  handleUpdatePassword: HandleUpdatePassword;
}

const FileItem: React.FC<Props> = ({
  item,
  handleDeleteFile,
  handleRetry,
  handleUpdatePassword,
}) => {
  const [parent] = useAutoAnimate();

  const deleteFileMutation = useDeleteFileMutation();

  const updateFilePasswordMutation = useUpdateFilePasswordMutation();

  return (
    <>
      <div
        className="flex w-full max-w-full flex-col gap-[6px] rounded-lg bg-gray-200 p-3"
        ref={parent}
      >
        <div className="flex w-full max-w-full flex-col justify-between gap-2 sm:flex-row sm:items-center">
          <div className="flex w-full items-center justify-between">
            <div className="caption h-4.5 break-words pr-2 sm:h-4 sm:pr-0">
              <FileNameCell name={item.name} />
            </div>
            <div className="flex flex-col items-center gap-2 sm:flex-row">
              <p
                className={cn(
                  'caption w-full flex-none text-dark-gray-300 sm:w-fit',
                  item.status === STATUS_FILE.UPLOADING && 'hidden'
                )}
              >
                {item.status === STATUS_FILE.FINISHED && prettyBytes(item.size)}
              </p>
              {item.status === STATUS_FILE.UPLOADING && (
                <p className="caption text-dark-gray-300">
                  {prettyBytes(item.size * (item.progress / 100))} /{' '}
                  {prettyBytes(item.size)}
                </p>
              )}
            </div>
          </div>
          <div
            className={cn(
              'flex w-full flex-row items-start gap-2 sm:w-fit sm:flex-row sm:items-center',
              item.status === STATUS_FILE.UPLOADING && 'flex-col'
            )}
          >
            {item.status === STATUS_FILE.FINISHED && !item.locked && (
              <div
                className={cn(
                  'flex w-full items-center justify-center gap-1 rounded bg-green-500 px-1.5 py-[5px] text-white sm:w-fit md:py-1.5'
                )}
              >
                <Check className="size-4 md:size-3.5" />
                <p className="caption md:caption-small">
                  <Trans id="Wgrane!">Wgrane!</Trans>
                </p>
              </div>
            )}

            {item.status === STATUS_FILE.ERROR && !item.locked && (
              <button
                className={cn(
                  'flex w-full items-center justify-center gap-1 rounded bg-red-400 px-1.5 py-[5px] text-white sm:w-fit md:py-1.5'
                )}
                type="button"
                onClick={() => handleRetry(item.id)}
              >
                <Refresh className="size-4 md:size-3.5" />
                <p className="caption md:caption-small whitespace-nowrap">
                  <Trans id="Wgraj ponownie">Wgraj ponownie!</Trans>
                </p>
              </button>
            )}
            {item.locked && (
              <div
                className={cn(
                  'caption md:caption-small flex w-full items-center justify-center gap-1 rounded bg-gray-400 px-1.5 py-[5px] text-dark-gray-400 sm:w-fit md:py-1.5'
                )}
              >
                <Padlock width={12} height={12} />
                <Trans id="Trwa analiza">Trwa analiza</Trans>
              </div>
            )}
            {item.status === STATUS_FILE.ADDED && !item.locked && (
              <div
                className={cn(
                  'caption md:caption-small flex w-full items-center justify-center gap-1 rounded bg-gray-400 px-1.5 py-[5px] text-dark-gray-400 sm:w-fit md:py-1.5'
                )}
              >
                <Trans id="Czeka">Czeka</Trans>
              </div>
            )}

            {!item.locked && (
              <Cross
                width={28}
                height={28}
                className={cn(
                  'w-full cursor-pointer rounded bg-gray-400 p-[7px] duration-150 hover:bg-gray-500 sm:w-fit',
                  item.file?.size &&
                    item.file?.size < 1048576 &&
                    item.status !== STATUS_FILE.FINISHED &&
                    item.status !== STATUS_FILE.ERROR &&
                    'cursor-not-allowed'
                )}
                onClick={async () => {
                  if (
                    item.file?.size &&
                    item.file?.size < 1048576 &&
                    item.status !== STATUS_FILE.FINISHED &&
                    item.status !== STATUS_FILE.ERROR
                  )
                    return;
                  handleDeleteFile(item);
                  if (item.id && !item.id.toString().includes('batch'))
                    await deleteFileMutation.mutateAsync(item.id);
                }}
              />
            )}
          </div>
        </div>
        {!item.locked && (
          <div className="h-[2px] bg-dark-gray-100 duration-100">
            <div
              className={cn(
                'h-[2px] rounded-lg bg-green-500 duration-500',
                item.status === STATUS_FILE.UPLOADING &&
                  'bg-blue-600 ease-in-out',
                item.status === STATUS_FILE.ERROR && 'bg-red-400'
              )}
              style={{
                width:
                  (item.status === STATUS_FILE.ERROR ? 100 : item.progress) +
                  '%',
              }}
            />
          </div>
        )}
        {item.passwordProtected && item.status === STATUS_FILE.FINISHED && (
          <Password
            defaultValue={item.password || ''}
            onBlur={(password) => {
              updateFilePasswordMutation.mutate({
                id: item.id,
                password,
              });
              handleUpdatePassword(item.id, password);
            }}
          />
        )}
      </div>
    </>
  );
};

const Password = ({
  onBlur,
  defaultValue,
}: {
  onBlur: (val: string) => void;
  defaultValue: string;
}) => {
  const [value, setValue] = useState(defaultValue || '');
  return (
    <PasswordInput
      label={'Hasło'}
      onBlur={() => onBlur(value)}
      value={value}
      success={!!value}
      onChange={(v) => setValue(v.currentTarget.value)}
      wrapperProps={{ className: 'mt-3' }}
    />
  );
};

export default FileItem;

const FileNameCell = ({ name }: { name: string }) => {
  const isXs = useMediaQuery({
    query: RESPONSIVE_QUERY.IS_XS,
  });

  if (shortenFile(name, isXs ? 50 : 20).isShortened) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger type="button" className="max-w-[100%] sm:max-w-full">
            <p className="caption max-w-[100%] cursor-text truncate font-medium">
              {shortenFile(name, isXs ? 28 : 12).name}
            </p>
          </TooltipTrigger>
          <TooltipContent>
            <p>{name}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <p className="caption font-medium">
      {shortenFile(name, isXs ? 50 : 20).name}
    </p>
  );
};
